import type {
  DeepPartial,
  SailPlotConfig,
  SailPlotMarkColorMode,
} from "@heiselanalytics/sailplot";
import {
  sailPlotCustomizedPageTitle,
  sailPlotFaviconDataUrl,
} from "./sailplotFavicon";

interface PreviewLink {
  id: "website" | "privacy" | "imprint";
  label: string;
  url: string;
}

interface PreviewLogoAction {
  id: string;
  type: "website" | "whatsapp" | "email" | "phone" | "link";
  label: string;
  value: string;
}

interface PreviewConfiguration {
  identityName: string;
  primaryColor: string;
  defaultTheme: "light" | "dark";
  languageMode: "de" | "en" | "both";
  markColorMode: SailPlotMarkColorMode;
  markCustomColor: string;
  startLineFlagColor: string;
  links: PreviewLink[];
  logoActions: PreviewLogoAction[];
  logoDataUrl: string;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const isHexColor = (value: unknown): value is string =>
  typeof value === "string" && /^#[0-9a-f]{6}$/iu.test(value);

const allowedOrigin = (origin: string) => {
  if (
    origin === "https://config.sailplot.app" ||
    origin === "https://sandbox.config.sailplot.app"
  ) {
    return true;
  }
  try {
    const url = new URL(origin);
    return (
      url.protocol === "http:" &&
      (url.hostname === "127.0.0.1" || url.hostname === "localhost")
    );
  } catch {
    return false;
  }
};

const validUrlOrEmpty = (value: unknown): value is string => {
  if (value === "") return true;
  if (typeof value !== "string" || value.length > 2048) return false;
  try {
    return ["http:", "https:"].includes(new URL(value).protocol);
  } catch {
    return false;
  }
};

const parseLinks = (value: unknown): PreviewLink[] | null => {
  if (!Array.isArray(value) || value.length > 3) return null;
  const links: PreviewLink[] = [];
  for (const item of value) {
    if (!isRecord(item)) return null;
    if (
      !(["website", "privacy", "imprint"] as const).includes(item.id as never)
    ) {
      return null;
    }
    if (
      typeof item.label !== "string" ||
      item.label.length > 40 ||
      !validUrlOrEmpty(item.url)
    ) {
      return null;
    }
    links.push(item as unknown as PreviewLink);
  }
  return links;
};

const parseLogoActions = (value: unknown): PreviewLogoAction[] | null => {
  if (value === undefined) return [];
  if (!Array.isArray(value) || value.length > 6) return null;
  const actions: PreviewLogoAction[] = [];
  for (const item of value) {
    if (!isRecord(item)) return null;
    if (
      typeof item.id !== "string" ||
      item.id.length > 80 ||
      !(["website", "whatsapp", "email", "phone", "link"] as const).includes(
        item.type as never,
      ) ||
      typeof item.label !== "string" ||
      item.label.length > 40 ||
      typeof item.value !== "string" ||
      item.value.length > 500
    ) {
      return null;
    }
    actions.push(item as unknown as PreviewLogoAction);
  }
  return actions;
};

const parsePreviewMessage = (value: unknown): PreviewConfiguration | null => {
  if (!isRecord(value) || value.type !== "sailplot-configurator-preview") {
    return null;
  }
  if (value.version !== 1 || !isRecord(value.configuration)) return null;
  const configuration = value.configuration;
  const links = parseLinks(configuration.links);
  const logoActions = parseLogoActions(configuration.logoActions);
  const validMarkModes: SailPlotMarkColorMode[] = [
    "sailplot",
    "primary",
    "yellow",
    "red",
    "custom",
  ];

  if (
    typeof configuration.identityName !== "string" ||
    configuration.identityName.length > 120 ||
    !isHexColor(configuration.primaryColor) ||
    !["light", "dark"].includes(configuration.defaultTheme as string) ||
    !["de", "en", "both"].includes(configuration.languageMode as string) ||
    !validMarkModes.includes(
      configuration.markColorMode as SailPlotMarkColorMode,
    ) ||
    !isHexColor(configuration.markCustomColor) ||
    !isHexColor(configuration.startLineFlagColor) ||
    !links ||
    !logoActions ||
    typeof configuration.logoDataUrl !== "string" ||
    !configuration.logoDataUrl.startsWith("data:image/svg+xml") ||
    configuration.logoDataUrl.length > 3_000_000
  ) {
    return null;
  }

  return {
    identityName: configuration.identityName,
    primaryColor: configuration.primaryColor,
    defaultTheme: configuration.defaultTheme as "light" | "dark",
    languageMode: configuration.languageMode as "de" | "en" | "both",
    markColorMode: configuration.markColorMode as SailPlotMarkColorMode,
    markCustomColor: configuration.markCustomColor,
    startLineFlagColor: configuration.startLineFlagColor,
    links,
    logoActions,
    logoDataUrl: configuration.logoDataUrl,
  };
};

const readableTextColor = (hex: string) => {
  const values = [1, 3, 5].map((index) =>
    Number.parseInt(hex.slice(index, index + 2), 16),
  );
  const luminance =
    (values[0] * 299 + values[1] * 587 + values[2] * 114) / 1000;
  return luminance >= 150 ? "#171717" : "#FFFFFF";
};

const linkById = (links: PreviewLink[], id: PreviewLink["id"]) =>
  links.find((link) => link.id === id)?.url || null;

const resolveLogoActions = (actions: PreviewLogoAction[]) =>
  actions.flatMap((action) => {
    const label = action.label.trim();
    const value = action.value.trim();
    if (!label || !value) return [];

    if (action.type === "email") {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/u.test(value)
        ? [{ label, url: `mailto:${value}` }]
        : [];
    }
    if (action.type === "phone" || action.type === "whatsapp") {
      if (!/^\+?[0-9 ()-]{6,30}$/u.test(value)) return [];
      const phone = value.replace(/[^0-9+]/gu, "");
      return [
        {
          label,
          url:
            action.type === "whatsapp"
              ? `https://wa.me/${phone.replace(/\D/gu, "")}`
              : `tel:${phone}`,
        },
      ];
    }
    try {
      const url = new URL(value);
      return ["http:", "https:"].includes(url.protocol)
        ? [{ label, url: url.toString() }]
        : [];
    } catch {
      return [];
    }
  });

export function configuratorPreviewOverrides(
  event: MessageEvent<unknown>,
): DeepPartial<SailPlotConfig> | null {
  if (!allowedOrigin(event.origin)) return null;
  const configuration = parsePreviewMessage(event.data);
  if (!configuration) return null;
  const primaryText = readableTextColor(configuration.primaryColor);

  return {
    branding: {
      appName: configuration.identityName || "Customized SailPlot",
      favicon: sailPlotFaviconDataUrl(configuration.primaryColor),
      partnerName: configuration.identityName || "Your Logo",
      partnerLabel: "",
      partnerLinks: resolveLogoActions(configuration.logoActions),
      partnerLogo: configuration.logoDataUrl,
      exportWatermarkLogo: configuration.logoDataUrl,
    },
    theme: {
      mode: configuration.defaultTheme,
      usePrimaryForBrandAccents: true,
      qrFinderColor: null,
      light: {
        primary: configuration.primaryColor,
        primaryText,
      },
      dark: {
        primary: configuration.primaryColor,
        primaryText,
      },
    },
    links: {
      qrCode: "https://sailplot.app/",
      website: linkById(configuration.links, "website"),
      privacy: linkById(configuration.links, "privacy"),
      imprint: linkById(configuration.links, "imprint"),
    },
    localization: {
      languageMode: configuration.languageMode,
    },
    objectColors: {
      markColorMode: configuration.markColorMode,
      markCustomColor:
        configuration.markColorMode === "custom"
          ? configuration.markCustomColor
          : null,
      startLineFlagColor: configuration.startLineFlagColor,
    },
    texts: {
      footerText: configuration.identityName || "Customized SailPlot",
    },
    pageTitle: sailPlotCustomizedPageTitle(configuration.identityName),
  };
}

export function mergeConfiguratorPreview(
  base: DeepPartial<SailPlotConfig>,
  overrides: DeepPartial<SailPlotConfig> | null,
): DeepPartial<SailPlotConfig> {
  if (!overrides) return base;
  return {
    ...base,
    ...overrides,
    branding: { ...base.branding, ...overrides.branding },
    theme: {
      ...base.theme,
      ...overrides.theme,
      light: { ...base.theme?.light, ...overrides.theme?.light },
      dark: { ...base.theme?.dark, ...overrides.theme?.dark },
    },
    links: { ...base.links, ...overrides.links },
    localization: { ...base.localization, ...overrides.localization },
    objectColors: { ...base.objectColors, ...overrides.objectColors },
    texts: { ...base.texts, ...overrides.texts },
  };
}
